import { BillingFieldsPartial } from "rivals/components/shared/Form/Address/types";
import {
  GiftPurchaseParams,
  RegistrationParams
} from "rivals/services/registration/types";
import { BillingParams } from "rivals/services/updateBillingInformation/types";
import { CARD_TYPE } from "rivals/shared/constants";
import { Address } from "rivals/shared/interfaces/User";
import { validateExpiration } from "rivals/shared/utils/time";

export const ZIP_CODE_REGEX = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

export interface ObickOptions {
  autoRenewalOptInStates: string[];
  createAccountAction?: (
    registrationParams: RegistrationParams,
    billingParams: BillingParams
  ) => void;
  enableOptIn?: boolean;
  formReady?: boolean;
  giftAction?: (
    giftingParams: GiftPurchaseParams,
    billingParams: BillingParams
  ) => void;
  initialState: FormState;
  obickSourceUrl: string;
  payPalEmail?: string | null;
  payPalToken?: string | null;
  payPalAddress?: Address | null;
  uniquePaypalTransId?: string;
  updateBillingAction?: (billingParams: BillingParams) => void;
}

export type DebitAgreedState = [boolean, (isDebitAgreed: boolean) => void];

const initialAutoCompleteState = {
  city: {
    error: "",
    validation: { required: true },
    value: ""
  },
  state: {
    error: "",
    validation: { required: false },
    value: ""
  },
  street1: {
    error: "",
    validation: { required: true },
    value: ""
  },
  street2: {
    error: "",
    validation: { required: false },
    value: ""
  },
  zip: {
    error: "",
    validation: { regex: ZIP_CODE_REGEX, required: true },
    value: ""
  }
};

const initialAddressState = {
  expirationDate: {
    error: "",
    validation: {
      custom: validateExpiration,
      regex: /(0[1-9]|10|11|12)\/[0-9]{2}$/,
      required: true
    },
    value: ""
  },
  firstName: {
    error: "",
    validation: { required: true },
    value: ""
  },
  lastName: {
    error: "",
    validation: { required: true },
    value: ""
  },
  ...initialAutoCompleteState
};

const initialCardState = {
  cardNumber: { error: "" },
  cvv: { error: "" }
};

export const initialState = {
  ...initialAddressState,
  ...initialCardState
};

type AddressKey = keyof Address;

export const mergeAddress = (address: Address): FormState => {
  for (const key in address) {
    const addressKey = key as AddressKey;
    initialState[addressKey].value = address[addressKey];
  }

  return initialState;
};

type CardFormState = typeof initialCardState;

export type FormState = typeof initialState;

export type AutoCompleteState = typeof initialAutoCompleteState;

export type AddressFormKey = keyof typeof initialAddressState;

export type AddressFormState = {
  [key in AddressFormKey]: {
    value: string;
    error: string;
    validation: {
      required: boolean;
      regex?: RegExp;
      custom?: (val: string) => boolean;
    };
  };
};

export type CardFormKey = keyof CardFormState;

export type FormKey = AddressFormKey | CardFormKey;

type AddressFormBulkEditAction = {
  type: "bulk-edit";
  payload: BillingFieldsPartial;
};

type AddressFormEditAction = {
  type: "edit";
  payload: { name: AddressFormKey; value: string };
};

type AddressFormValidateAction = {
  type: "validate";
  payload: { name: AddressFormKey; value: string };
};

type CardFormAction = {
  type: "card-error" | "clear-card-error";
  payload: { name: CardFormKey; error?: string };
};

type ErrorAction = {
  type: "address-error";
  payload: { name: AddressFormKey; error: string };
};

type ResetFormAction = {
  type: "reset-form";
};

export type FormAction =
  | AddressFormBulkEditAction
  | AddressFormEditAction
  | AddressFormValidateAction
  | CardFormAction
  | ErrorAction
  | ResetFormAction;

interface CheckoutInputStatus {
  generatingToken: boolean;
}

type CardError = "missing" | "invalid" | "incomplete";

export interface CardEvent {
  hasFocus: boolean;
  dirty: boolean;
  errors?: { [T in CardError]: string };
  leadingDigits?: string;
  paymentType?: CARD_TYPE;
}

interface CardInputField {
  onStatusUpdate: (callback: (e: CardEvent) => void) => void;
}

export interface CheckoutInput {
  status: () => CheckoutInputStatus;
  createToken: () => Promise<string>;
  cardNumber: () => CardInputField;
  cvv: () => CardInputField;
}

export interface CheckoutField {
  input: CheckoutInput;
}

interface CheckoutArgs {
  theme: {
    iframe: {
      cardNumber: {
        style: (field: CheckoutField) => { ["::disabled"]: boolean };
      };
      cvv: {
        style: (field: CheckoutField) => { ["::disabled"]: boolean };
      };
    };
  };
}

export interface Checkout {
  input: (args: CheckoutArgs) => Promise<CheckoutInput>;
}

export enum FORM_TEXT {
  REQUIRED_ERROR = "Required",
  INVALID_ERROR = "Invalid",
  CARD_ERROR = "Invalid Card Number",
  CVV_ERROR = "Invalid CVV",
  FIRST_NAME = "First Name",
  LAST_NAME = "Last Name",
  STREET_1 = "Street 1",
  STREET_2 = "Street 2",
  EXPIRATION = "MM/YY",
  CITY = "City",
  STATE = "State",
  ZIP = "Zip Code"
}
